import { ArrowsClockwiseIcon, ChatCenteredIcon, ProhibitIcon, TimerIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import type { StudentTestContent } from "p6m-p6u";
import { useEffect } from "react";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import { ClassActivityCategory, classActivityColors } from "../../constants/ClassActivityConstants";
import { useTestCreationContext } from "../../context/TestCreationContext";
import { ShareModes } from "../../enums/shareFlow";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { getStudentTestById } from "../../networking/tests";
import { formatDateWithTime } from "../../utils/dateUtils";
import { AreaButton, ButtonColorVariant, ButtonVariant, IconPosition } from "../teacherArea/AreaButton";

const SUBMISSIONS_POLL_INTERVAL_MS = 60_000;

export interface SharedOnlineTestDetailsProps {
    sharedTestContent?: StudentTestContent | null;
    onShare?: () => void;
    onGrade?: () => void;
    onEditDetails?: () => void;
    onViewSubmissions?: () => void;
}

export const SharedOnlineTestDetails = (props: SharedOnlineTestDetailsProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const { setStudentTestContentWrapper } = useTestCreationContext();

    const testId = props.sharedTestContent?.id;

    useEffect(() => {
        if (!testId) return;

        let intervalId: ReturnType<typeof setInterval> | undefined;
        let isCancelled = false;

        const refreshSubmissions = async () => {
            try {
                const updated = await getStudentTestById(testId);
                if (isCancelled || !updated) return;

                setStudentTestContentWrapper((prev) => ({
                    ...prev,
                    [testId]: { ...prev[testId], ...updated },
                }));
            } catch (error) {
                console.error("Failed to refresh submissions for test", testId, error);
            }
        };

        const startPolling = () => {
            if (intervalId) return;
            refreshSubmissions();
            intervalId = setInterval(refreshSubmissions, SUBMISSIONS_POLL_INTERVAL_MS);
        };

        const stopPolling = () => {
            clearInterval(intervalId);
            intervalId = undefined;
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                startPolling();
            } else {
                stopPolling();
            }
        };

        handleVisibilityChange();
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            isCancelled = true;
            stopPolling();
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [testId, setStudentTestContentWrapper]);

    const handleShare = () => {
        props.onShare?.();
    };

    const t_oclock = t("o'clock", { _tags: "ClassActivity" });
    const t_onlineTask = t("Online Task", { _tags: "ClassActivity" });
    const t_messageForStudents = t("Message for your students", { _tags: "ClassActivity" });
    const t_dueDate = t("Deadline", { _tags: "ClassActivity" });
    const t_processingTime = t("Editing Time", { _tags: "ClassActivity" });
    const t_currentSubmissions = t("Current Answers", { _tags: "ClassActivity" });
    const t_share = t("Share", { _tags: "ClassActivity" });
    const t_grade = t("Grade Answers", { _tags: "ClassActivity" });
    const t_editMessage = t("Edit message", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_editDueDate = t("Edit due date", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_editDuration = t("Edit duration", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_anytime = t("anytime", { _tags: "ClassActivity" });
    const t_noMessage = t("none", { _tags: "ClassActivity" });
    const t_unlimited = t("unlimited", { _tags: "ClassActivity" });
    const t_unlimitedRepeatable = t("Unlimited replays", { _tags: "ClassActivity" });
    const t_answersNotStored = t("Answers will not be saved.", { _tags: "ClassActivity" });

    const colors = classActivityColors[ClassActivityCategory.TEST];

    const responsiveRowClass = `flex ${getStyleBasedOnBreakpoint(
        Breakpoint.MD,
        "flex-row items-center gap-xs",
        "flex-col items-start"
    )}`;

    // Trimmed because legacy records may hold whitespace-only comments.
    const messageForStudents = props.sharedTestContent?.teacherComment?.trim();
    const dueDate = formatDateWithTime(props.sharedTestContent?.dueDate, t_oclock);
    const processingTime = props.sharedTestContent?.timeLimit;
    const submissionsCount = props.sharedTestContent?.resultsCount ?? 0;
    const canGrade = submissionsCount > 0;
    const isPractice = props.sharedTestContent?.shareMode === ShareModes.PRACTICE;

    const renderEditableValue = (value: string, ariaLabel: string, extraButtonClass = "") => {
        const onEdit = props.onEditDetails;
        if (!onEdit) {
            return <span className="text-l text-p6-grey-dark">{value}</span>;
        }
        return (
            <button
                type="button"
                aria-label={ariaLabel}
                onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                }}
                className={`flex items-center justify-center cursor-pointer gap-xs ${extraButtonClass}`}
            >
                <span className="text-l text-p6-grey-dark">{value}</span>
                <PhaseSixIcon
                    name="edit-avatar"
                    className="text-xl text-p6-grey-dark hover:text-p6-grey-darkest shrink-0"
                />
            </button>
        );
    };

    const renderMessageForStudents = () => (
        <div className={responsiveRowClass}>
            <div className="flex items-center gap-xs">
                <PhosphorIcon
                    phosphorIcon={ChatCenteredIcon}
                    color="text-p6-grey-dark"
                    fontSize="text-xl"
                />
                <span className="font-semibold text-l text-p6-grey-dark">{t_messageForStudents}</span>
            </div>
            {renderEditableValue(messageForStudents || t_noMessage, t_editMessage, "text-left")}
        </div>
    );

    const renderDueDate = () => (
        <div className={responsiveRowClass}>
            <div className="flex items-center gap-xs">
                <PhaseSixIcon
                    name="calendar-cross"
                    className="text-xl text-p6-grey-dark shrink-0"
                />
                <span className="font-semibold text-l text-p6-grey-dark">{t_dueDate}</span>
            </div>
            {renderEditableValue(dueDate || t_anytime, t_editDueDate)}
        </div>
    );

    const renderProcessingTime = () => (
        <div className={responsiveRowClass}>
            <div className="flex items-center gap-xs">
                <PhosphorIcon
                    phosphorIcon={TimerIcon}
                    color="text-p6-grey-dark"
                    fontSize="text-xl"
                />
                <span className="font-semibold text-l text-p6-grey-dark">{t_processingTime}</span>
            </div>
            {renderEditableValue(processingTime ? `${processingTime} h` : t_unlimited, t_editDuration)}
        </div>
    );

    const renderPracticeRow = (icon: typeof ArrowsClockwiseIcon, label: string) => (
        <div className={responsiveRowClass}>
            <div className="flex items-center gap-xs">
                <PhosphorIcon
                    phosphorIcon={icon}
                    color="text-p6-grey-dark"
                    fontSize="text-xl"
                />
                <span className="font-semibold text-l text-p6-grey-dark">{label}</span>
            </div>
        </div>
    );

    const renderSubmissionsCount = () => {
        return (
            <div
                className={`flex flex-col items-center gap-xs shrink-0 ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "",
                    "w-full"
                )}`}
            >
                <span className="font-semibold text-l text-p6-grey-dark self-start">{t_currentSubmissions}</span>
                <div
                    className={`bg-background-light rounded-medium w-full h-[100px] flex items-center justify-center ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "min-w-[100px]",
                        ""
                    )}`}
                >
                    <span className="font-semibold text-xxl text-p6-grey-dark">{submissionsCount}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white border border-p6-grey-light rounded-medium overflow-hidden">
            <div className="px-m py-s">
                <span className="font-semibold text-l text-p6-grey-dark">{t_onlineTask}</span>
            </div>

            <div className={`border-t-2 ${colors.border} px-m py-s`}>
                {isPractice ? (
                    <div className="flex flex-col gap-s">
                        {renderMessageForStudents()}
                        {renderPracticeRow(ArrowsClockwiseIcon, t_unlimitedRepeatable)}
                        {renderPracticeRow(ProhibitIcon, t_answersNotStored)}
                    </div>
                ) : (
                    <div
                        className={`flex gap-s ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "flex-row",
                            "flex-col-reverse"
                        )}`}
                    >
                        <div className="flex flex-col gap-s flex-1">
                            {renderMessageForStudents()}
                            {renderDueDate()}
                            {renderProcessingTime()}
                        </div>
                        {renderSubmissionsCount()}
                    </div>
                )}
                <div
                    className={`flex gap-s mt-s flex-wrap items-center ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        isPractice ? "flex-row justify-end" : "flex-row justify-between",
                        "flex-row justify-end"
                    )}`}
                >
                    <AreaButton
                        variant={ButtonVariant.SECONDARY}
                        colorVariant={ButtonColorVariant.HIGH_CONTRAST}
                        iconName="share"
                        iconPosition={IconPosition.RIGHT}
                        role="button"
                        onClick={handleShare}
                    >
                        {t_share}
                    </AreaButton>
                    {!isPractice && (
                        <AreaButton
                            variant={ButtonVariant.PRIMARY}
                            role="button"
                            disabled={!canGrade}
                            onClick={props.onGrade}
                        >
                            {t_grade}
                        </AreaButton>
                    )}
                </div>
            </div>
        </div>
    );
};
